import React, { useState, useEffect } from "react";
import {useLoaderData, Link} from 'react-router-dom';
import Group2 from "../components/Group2";
import { getSearchContent, updateBlockOrderInDB, initialiseBaseSkins } from "../Users";
import { useUser } from "../components/UserContext";
import '../texteditstyle.css';
import PersonIcon from '@mui/icons-material/Person';
import LastPageIcon from '@mui/icons-material/LastPage';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

export async function loader({ request }) {
  const url = new URL(request.url);
  const q = url.searchParams.get("q");
  const p = url.searchParams.get("p");
  const f = url.searchParams.get("f");
  const s = url.searchParams.get("s");

  const searchContent = await getSearchContent(q, p, f, s);
  if (!searchContent || (s !== 'rec' && s !== 'rel') || (f !== 'm' && f !== 'e') || (Number.isInteger(Number(p)) === false)) {
    throw new Response("", { status: 404, statusText: "Not Found" });
  }

  return { q, p, f, s, searchContent };
}

export default function Search() {
  const {q, p, f, s, searchContent} = useLoaderData();
  const {currentUser} = useUser();
  const [groups, setGroups] = useState([]);
  const [displayedGroups, setDisplayedGroups] = useState([]);
  const [heights, setHeights] = useState([]);
  const groupDisplayOrder = true;
  
  const recalcTimerRef = React.useRef(null);
  const recalcArgsRef  = React.useRef(null);
  // optional: keep a ref to the latest groups so the timeout sees fresh state
  const groupsRef = React.useRef(groups);
  React.useEffect(() => { groupsRef.current = groups; }, [groups]);

  useEffect(() => { //initialise
    document.title = `"${q}" - Search`;
    initialiseBaseSkins(currentUser.id);
    const initializegroupStartingHeights = () => {
      
      let initializedHeights = [];
      for (let i = 0; i < searchContent.groups.length; i++) {
        initializedHeights.push(40)
      }
      setHeights(initializedHeights);
    };

    if (searchContent.groups.length >= 0) {
      setGroups([...searchContent.groups])
      if (searchContent.groups.length > 0) {
        initializegroupStartingHeights();
      }
    } 
  }, [searchContent, currentUser.id, q])

  useEffect(() => { //reverse order
    if (!groupDisplayOrder) {
      setDisplayedGroups(groups.toReversed());
    } else {
      setDisplayedGroups(groups);
    }
  }, [groupDisplayOrder, groups])

  function updateGroupHeight(order, newHeight) {
    setHeights(prevHeights => {
      const updatedHeights = [...prevHeights];
      updatedHeights[order] = newHeight;
      return updatedHeights;
    });
  }  
  
  function cleanOrder(things) {
    // Deep copy the array and the objects inside it
    return things.map((blockItem, index) => ({
      ...blockItem, // Spread the existing properties of the block
      order: index  // Update the order
    }));
  }  
  
  function recalcBlockOrder(groupId, groupOrder, originalSH) {
    // cancel any pending run
    if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);

    // save the latest args
    recalcArgsRef.current = [groupOrder, originalSH];

    // schedule the heavy work for after 100ms of silence
    recalcTimerRef.current = setTimeout(() => {
      const [gOrder, oSH] = recalcArgsRef.current;

      // ===== YOUR ORIGINAL FUNCTION CONTENT (use oSH/gId/gOrder) =====
      const SHOnly = [];
      const CSOnly = [];
      const SHMostlyInOrder = [];
      const placementsPrettyMuch = [];
      const finalPlacements = []; 

      for (let i = 0; i < oSH.length; i++) {
        SHOnly.push(oSH[i].SH);
        CSOnly.push(oSH[i].CS);
      }
      const SHDec = [...SHOnly];
      const processedIndexes = new Array(SHOnly.length).fill(false); 
      for (let i = 0; i < oSH.length; i++) {
        const newLSH = Math.min(...SHDec);  
        const iOfNLSH = SHOnly.findIndex((sh, idx) => sh === newLSH && !processedIndexes[idx]); 
        SHMostlyInOrder.push(newLSH);  
        placementsPrettyMuch.push(iOfNLSH); 
        const fakeIndex = SHDec.indexOf(newLSH); 
        SHDec.splice(fakeIndex, 1); 
        processedIndexes[iOfNLSH] = true;  
      }
      for (let i = 0; i < oSH.length; i++) {
        const dupeIndexes = [];
        SHMostlyInOrder.forEach((SH, index) => {
          if (SH === SHMostlyInOrder[i]) dupeIndexes.push(index);
        });
        if (dupeIndexes.length > 1) {
          const dupePlacementIndexes = [];
          dupeIndexes.forEach((index) => {
            // const originalIndex = placementsPrettyMuch[index];
            dupePlacementIndexes.push(index); 
          });
          const sortedDupeIndexes = dupePlacementIndexes.sort((a, b) => {
            const originalIndexA = placementsPrettyMuch[a];  
            const originalIndexB = placementsPrettyMuch[b]; 
            return CSOnly[originalIndexA] - CSOnly[originalIndexB];  
          });
          sortedDupeIndexes.forEach((sortedIndex) => {
            finalPlacements.push(placementsPrettyMuch[sortedIndex]);
          });
          i += dupeIndexes.length - 1;
        } else {
          finalPlacements.push(placementsPrettyMuch[i]);
        }
      }

      const needsReorder = finalPlacements.some((place, index) => place !== index);

      if (needsReorder) {
        const updatedSH = [...oSH];
        const reorderedSH = [];
        finalPlacements.forEach((placement, i) => {
          reorderedSH[i] = updatedSH[placement];
        });

        // you had this before; keeping as-is
        finalPlacements.pop();

        // use latest groups via ref to avoid stale closures
        const currentGroups = groupsRef.current;
        const group = currentGroups?.[gOrder];
        if (!group) return;

        updateBlockOrderInDB(group.id, finalPlacements, group.blocks?.length ?? 0);

        // update UI
        setTimeout(() => {
          setGroups(prevGroups => {
            const updatedGroups = [...prevGroups];
            const groupToUpdate = updatedGroups[gOrder];
            if (!groupToUpdate) return prevGroups;

            const movingBlocks = [...groupToUpdate.blocks];
            let reorderedBlocks = [];
            finalPlacements.forEach((placement, i) => {
              reorderedBlocks[i] = movingBlocks[placement];
            });
            reorderedBlocks = cleanOrder(reorderedBlocks);

            updatedGroups[gOrder] = { ...groupToUpdate, blocks: reorderedBlocks };
            return updatedGroups;
          });
        }, 0);
      }
      // ===== END original content =====
    }, 30);

    // immediate, lightweight return for every call pre-debounce
    return originalSH;
  }

  return (
    <div>
      <div className="search-page-head">
        <div className="search-results-container">{searchContent.length > 0 ? <p>showing results {(p - 1) * 50 + 1} - {(p - 1) * 50 + displayedGroups.length + searchContent.users.length} out of {searchContent.length} for "{q}"</p> : <p>sorry, no results for "{q}"</p>}</div>
        <div className="search-page-navigator">
          {Number(p) !== 1 && searchContent.length > 50 && <Link to={`/search?q=${encodeURIComponent(q)}&p=1&f=${f}&s=${s}`} style={{left:0}}><FirstPageIcon className="menu-icon"/></Link >}
          {Number(p) !== 1 && searchContent.length > 50 && <Link to={`/search?q=${encodeURIComponent(q)}&p=${Number(p) - 1}&f=${f}&s=${s}`} style={{left:"30px"}}><NavigateBeforeIcon className="menu-icon"/></Link >}
          {Number(p) !== Math.ceil(searchContent.length / 50) && searchContent.length > 50 && <Link to={`/search?q=${encodeURIComponent(q)}&p=${Number(p) + 1}&f=${f}&s=${s}`} style={{right:"30px"}}><NavigateNextIcon className="menu-icon"/></Link >}
          {Number(p) !== Math.ceil(searchContent.length / 50) && searchContent.length > 50 && <Link to={`/search?q=${encodeURIComponent(q)}&p=${Math.ceil(searchContent.length / 50)}&f=${f}&s=${s}`} style={{right:0}}><LastPageIcon className="menu-icon"/></Link >}
        </div>
        {searchContent.length > 0 && 
        <div className="search-filter-container">
          <Link to={`/search?q=${encodeURIComponent(q)}&p=1&f=${f === "e" ? "m" : "e"}&s=${s}`}>
            {f === "e" ? <>showing everyone's stuff</> : <>showing only your stuff</>}
          </Link>
          <Link to={`/search?q=${encodeURIComponent(q)}&p=1&f=${f}&s=${s === "rec" ? "rel" : "rec"}`}>
            {s === "rel" ? <>sorting by relevancy</> : <>sorting by recency</>}
          </Link>
        </div>
        }
        
      </div>

      <div id="user-area">
        {searchContent.users.map((item) => {
            return (
                <div className="people-item">
                    {item.pfp? <img className="people-item-image" src={item.pfp} alt="mock pfp" /> :
                    <div className="people-item-image"><PersonIcon className="people-item-default-image"/></div>}
                    <Link to={`/profile/${item.id}`} class="people-item-header">
                        <h4>{item.username}</h4>
                    </Link>
                    <div className="people-item-footer" />
                </div>
            )
        })}
      </div>

      <div id="block-area">
        {displayedGroups.map((groupItem, index) => (
          <Group2
            key={`${groupItem.id}-${q}-${p}`}
            groupId={groupItem.id}
            groupOrder={groupItem.order}
            title={groupItem.title}
            blocks={groupItem.blocks}
            height={heights[groupItem.order]}
            onHeightChange={updateGroupHeight}
            recalcOrder={recalcBlockOrder}
            isProfile={groupItem.isProfile}
            pageAuthor={groupItem.authorId}
            currentUser={currentUser.id}
            pageSkin={groupItem.skin}
            isSearch={true}
            pageCode={groupItem.pageCode}
            searchTerm={q}
            filter={f}
          />
        ))}
      </div>
      <div id="space"></div>
    </div>
  );
}